import {
  MAX_GET_AUTH_USERS,
  type AuthUser,
} from "./authUsers";
import { fetchAuthUsers } from "./getAuthUsers";

const DEBOUNCE_MS = 200;

type EventKey = string;
type CacheKey = string;

const cache = new Map<CacheKey, AuthUser | null>();
const pending = new Map<EventKey, Set<string>>();
const waiters = new Map<CacheKey, Array<(user: AuthUser | null) => void>>();
const timers = new Map<EventKey, ReturnType<typeof setTimeout>>();

function eventKey(accountId: string, eventId: string): EventKey {
  return `${accountId}/${eventId}`;
}

function cacheKey(accountId: string, eventId: string, uid: string): CacheKey {
  return `${eventKey(accountId, eventId)}:${uid}`;
}

function parseEventKey(key: EventKey): { accountId: string; eventId: string } {
  const slash = key.indexOf("/");
  return {
    accountId: key.slice(0, slash),
    eventId: key.slice(slash + 1),
  };
}

function resolveWaiters(key: CacheKey, user: AuthUser | null): void {
  const resolvers = waiters.get(key);
  waiters.delete(key);
  if (!resolvers) {
    return;
  }
  for (const resolve of resolvers) {
    resolve(user);
  }
}

function takeQueued(key: EventKey): string[] {
  const set = pending.get(key);
  if (!set || set.size === 0) {
    pending.delete(key);
    return [];
  }
  const all = [...set];
  const batch = all.slice(0, MAX_GET_AUTH_USERS);
  if (batch.length >= all.length) {
    pending.delete(key);
  } else {
    pending.set(key, new Set(all.slice(MAX_GET_AUTH_USERS)));
  }
  return batch;
}

async function flush(key: EventKey): Promise<void> {
  const { accountId, eventId } = parseEventKey(key);
  const uids = takeQueued(key);
  if (uids.length === 0) {
    return;
  }

  try {
    const { users } = await fetchAuthUsers({ accountId, eventId, uids });
    const byUid = new Map(users.map((user) => [user.uid, user]));
    for (const uid of uids) {
      const value = byUid.get(uid) ?? null;
      const keyForUid = cacheKey(accountId, eventId, uid);
      cache.set(keyForUid, value);
      resolveWaiters(keyForUid, value);
    }
  } catch (error) {
    for (const uid of uids) {
      resolveWaiters(cacheKey(accountId, eventId, uid), null);
    }
    console.error("getAuthUsers batch failed", error);
  }

  if ((pending.get(key)?.size ?? 0) > 0) {
    scheduleFlush(accountId, eventId);
  }
}

function scheduleFlush(accountId: string, eventId: string): void {
  const key = eventKey(accountId, eventId);
  const existing = timers.get(key);
  if (existing) {
    clearTimeout(existing);
  }
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      void flush(key);
    }, DEBOUNCE_MS)
  );
}

export function peekAuthUserCache(
  accountId: string,
  eventId: string,
  uid: string
): AuthUser | null | undefined {
  const key = cacheKey(accountId, eventId, uid);
  if (!cache.has(key)) {
    return undefined;
  }
  return cache.get(key);
}

export function requestAuthUser(
  accountId: string,
  eventId: string,
  uid: string
): Promise<AuthUser | null> {
  const trimmed = uid.trim();
  if (!trimmed) {
    return Promise.resolve(null);
  }

  const key = cacheKey(accountId, eventId, trimmed);
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key)!);
  }

  return new Promise((resolve) => {
    const existing = waiters.get(key);
    if (existing) {
      existing.push(resolve);
    } else {
      waiters.set(key, [resolve]);
    }

    const pendingKey = eventKey(accountId, eventId);
    const set = pending.get(pendingKey) ?? new Set<string>();
    set.add(trimmed);
    pending.set(pendingKey, set);
    scheduleFlush(accountId, eventId);
  });
}
